import React, { useEffect, useState } from "react";
import { Search, Package } from "lucide-react";
import { api } from "../lib/api";
import { formatINR as fmtMoney } from "../lib/format";
import ModuleEmptyState from "../components/app/ModuleEmptyState";

export default function Products() {
  const [items, setItems] = useState([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const t = setTimeout(async () => {
      setLoading(true);
      try {
        const { data } = await api.get(`/products${search ? `?search=${encodeURIComponent(search)}` : ""}`);
        setItems(data);
      } finally { setLoading(false); }
    }, 200);
    return () => clearTimeout(t);
  }, [search]);

  return (
    <div className="max-w-7xl mx-auto" data-testid="products-page">
      <p className="text-xs font-bold uppercase tracking-[0.2em] text-orange-500/80 mb-2">Products</p>
      <h1 className="text-3xl sm:text-4xl font-semibold tracking-tight text-zinc-50" style={{ fontFamily: "var(--font-heading)" }}>
        Product Intelligence
      </h1>
      <p className="mt-2 text-zinc-400 max-w-2xl">Catalog ranked by revenue — across every store and marketplace.</p>

      {!loading && items.length === 0 && !search ? (
        <ModuleEmptyState
          icon={Package}
          title="No products imported"
          description="Import order history and Milkath will deduplicate your catalog by SKU and rank every product by revenue."
          testid="products-empty"
        />
      ) : (
      <>
      <div className="mt-8 relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-zinc-500" />
        <input
          value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search products…"
          className="w-full bg-zinc-900 border border-zinc-800 rounded-lg pl-9 pr-3 py-2 text-sm text-zinc-100 focus:outline-none focus:ring-2 focus:ring-orange-600/40 focus:border-orange-600"
          data-testid="products-search"
        />
      </div>

      <div className="mt-6 rounded-2xl border border-zinc-800 bg-zinc-900/40 overflow-hidden">
        {loading ? (
          <div className="p-8 text-center text-sm text-zinc-500">Loading…</div>
        ) : items.length === 0 ? (
          <div className="p-16 text-center" data-testid="products-empty">
            <div className="mx-auto h-14 w-14 rounded-2xl bg-orange-600/10 border border-orange-600/20 flex items-center justify-center mb-4">
              <Package className="h-6 w-6 text-orange-500" />
            </div>
            <h3 className="text-lg font-medium text-zinc-100">No products yet</h3>
            <p className="mt-2 text-sm text-zinc-400">Import order history to populate this list.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-[10px] uppercase tracking-[0.16em] text-zinc-500 border-b border-zinc-800">
                  <th className="px-5 py-3 font-medium">Product</th>
                  <th className="px-5 py-3 font-medium">SKU</th>
                  <th className="px-5 py-3 font-medium">Category</th>
                  <th className="px-5 py-3 font-medium text-right">Units sold</th>
                  <th className="px-5 py-3 font-medium text-right">Revenue</th>
                  <th className="px-5 py-3 font-medium text-right">Last price</th>
                </tr>
              </thead>
              <tbody>
                {items.map((p) => (
                  <tr key={p.product_id} className="border-b border-zinc-800/60 hover:bg-zinc-900/60" data-testid={`product-row-${p.product_id}`}>
                    <td className="px-5 py-3 text-zinc-100 truncate max-w-[280px]">{p.name}</td>
                    <td className="px-5 py-3 text-zinc-400">{p.sku || "—"}</td>
                    <td className="px-5 py-3 text-zinc-400">{p.category_name || "—"}</td>
                    <td className="px-5 py-3 text-right tabular-nums text-zinc-200">{p.total_quantity_sold}</td>
                    <td className="px-5 py-3 text-right tabular-nums text-orange-400 font-medium">{fmtMoney(p.total_revenue)}</td>
                    <td className="px-5 py-3 text-right tabular-nums text-zinc-300">{fmtMoney(p.last_unit_price)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
      </>
      )}
    </div>
  );
}
